import React, { Component } from "react";
import PropTypes from "prop-types";

import OldMedicineService from "./OldMedicineService";
import "../../styles/oldMedicines.scss";
import MedicinesEmpty from "./MedicinesEmpty";

/**
 * The "Old" medicines view — the legacy medication-tracking feed, READ ONLY.
 *
 * SHAPE FOLLOWS THE DATA. Each entry is a point-in-time POST of the WHOLE
 * medicine list, with one author and one timestamp — not a medicine with its
 * own lifecycle. So this is a feed of snapshots, not a table: a flat table
 * would have to invent a per-medicine identity the payload does not carry, and
 * would repeat every still-ongoing drug once per snapshot as if they were
 * separate records.
 *
 * Every code in the payload is a reference id, so the two master lists are
 * loaded alongside it. Rendering a raw REFLD198 on a clinical screen would be
 * worse than rendering nothing.
 */

const service = new OldMedicineService();
const PAGE_SIZE = 3;

/* MEDICINE_TIMEPERIOD codes the legacy paints RED (Medications.js:220-224).
   They are a clinical signal — the drug is stopped or paused — not decoration,
   so the redesign keeps them marked. */
const ALERT_PERIODS = ["REFLD198", "REFLD197"];

const byValue = (list, value) =>
  ((list || []).find((e) => e && e.value === value) || {}).label || "";

/* Salt units are keyed by `code`, NOT `value` like every dataTables list
   (Medications.js:117-124). Getting this wrong silently drops every unit. */
const byCode = (list, code) =>
  ((list || []).find((e) => e && e.code === code) || {}).label || "";

/** "LOSARTAN POTASSIUM 25 mg, AMLODIPINE 5 mg" */
const saltText = (saltTherapy, units) => {
  const parts = [];
  (saltTherapy || []).forEach((t) =>
    (t.salt || []).forEach((s) =>
      (s.saltComposition || []).forEach((c) => {
        const bits = [
          c.saltName || "",
          c.strength == null ? "" : String(c.strength),
          c.uom ? byCode(units, c.uom) : "",
        ].filter(Boolean);
        if (bits.length) parts.push(bits.join(" "));
      })
    )
  );
  return parts.join(", ");
};

const fmtWhen = (v) => {
  if (v == null || v === "") return "";
  const d = new Date(typeof v === "number" ? v : String(v));
  if (isNaN(d.getTime())) return String(v);
  return d.toLocaleString(undefined, {
    day: "numeric", month: "short", year: "numeric",
    hour: "numeric", minute: "2-digit",
    /* Explicit. `undefined` defers to the machine locale, and en-GB renders
       24-hour — this showed "17:51" where the legacy shows "5:51 PM". */
    hour12: true,
  });
};

class OldMedicines extends Component {
  constructor(props) {
    super(props);
    this.state = {
      entries: [],
      total: null,
      page: 1,
      loading: true,
      loadingMore: false,
      failed: false,
      refs: {},
      units: [],
    };
  }

  componentDidMount() {
    const { patientId } = this.props;
    /* Reference lists are patient-agnostic and fired alongside the feed rather
       than before it: a failed lookup should cost a label, not the whole view. */
    service.referenceLists(
      (res) => this.setState({ refs: res || {} }),
      () => this.setState({ refs: {} })
    );
    service.saltUnits(
      (res) => this.setState({ units: Array.isArray(res) ? res : (res && res.salts) || [] }),
      () => this.setState({ units: [] })
    );
    service.count(
      patientId,
      (res) => this.setState({ total: typeof res === "number" ? res : Number(res) || 0 }),
      () => {}
    );
    this._page(1);
  }

  _page(pageNo) {
    const { patientId } = this.props;
    service.list(
      patientId,
      pageNo,
      PAGE_SIZE,
      (res) => {
        const rows = Array.isArray(res) ? res : (res && res.data) || [];
        this.setState((st) => ({
          entries: pageNo === 1 ? rows : st.entries.concat(rows),
          page: pageNo,
          loading: false,
          loadingMore: false,
          failed: false,
        }));
      },
      () => this.setState({ loading: false, loadingMore: false, failed: true })
    );
  }

  _more = () => {
    this.setState({ loadingMore: true });
    this._page(this.state.page + 1);
  };

  _line(item, i) {
    const { refs, units } = this.state;
    const formulation = byValue(refs.MEDICINE_FORMULATIONS, item.formulation);
    const name =
      (item.medicineCode && item.medicineCode.label
        ? item.medicineCode.label.toUpperCase()
        : item.medicineName) || "";
    const salts = saltText(item.saltTherapy, units);
    const freq = [
      item.morningFrequency || 0,
      item.afternoonFrequency || 0,
      item.eveningFrequency || 0,
    ].join("-");
    const instructions = byValue(refs.MEDICINE_INSTRUCTIONS, item.instructions);
    /* The legacy compares the resolved LABEL to "Other" (Medications.js:227),
       not the code — so the same comparison is kept rather than guessing at a
       constant. */
    const periodLabel = byValue(refs.MEDICINE_TIMEPERIOD, item.timePeriod);
    const period =
      periodLabel === "Other" ? item.otherDuration || "" : periodLabel;
    const alert = ALERT_PERIODS.indexOf(item.timePeriod) !== -1;

    return (
      <li className="oldmed-line" key={i}>
        <span className="oldmed-name">
          {formulation ? `${formulation} ` : ""}
          <b>{name}</b>
          {salts ? <span className="oldmed-salt"> ({salts})</span> : null}
        </span>
        <span className="oldmed-meta">
          <span className="oldmed-freq">{freq}</span>
          {instructions ? <span>{instructions}</span> : null}
          {period ? (
            <span className={alert ? "oldmed-period is-alert" : "oldmed-period"}>
              {period}
            </span>
          ) : null}
          {item.description ? <span>{item.description}</span> : null}
        </span>
      </li>
    );
  }

  render() {
    const { entries, total, loading, loadingMore, failed } = this.state;
    const exhausted = total != null && entries.length >= total;

    if (loading) return <p className="oldmed-msg">Loading medication tracking&hellip;</p>;
    if (failed)
      return <p className="oldmed-msg is-error">Couldn&rsquo;t load medication tracking.</p>;
    if (!entries.length)
      /* The same empty state the New view draws. A bare grey line here read as
         a failure — the one above it, for a load that really did fail, looked
         almost identical. */
      return (
        <MedicinesEmpty
          title="No medication tracking recorded"
          body={
            <>
              Nothing was tracked for this patient in the legacy view. Medicines
              added in the New view live there, not here.
            </>
          }
        />
      );

    return (
      <div className="oldmed">
        {/* Said once, at the top: this whole view is history and cannot be
            edited here. Better than nine disabled controls. */}
        <p className="oldmed-note">
          Legacy medication tracking &mdash; view only. Edits are made in the
          New view.
        </p>

        <ol className="oldmed-list">
          {entries.map((e, i) => (
            <li className="oldmed-entry" key={e.id || `${e.dateCreated}-${i}`}>
              <div className="oldmed-head">
                <span className="oldmed-when">{fmtWhen(e.dateCreated)}</span>
                {e.createdByName ? (
                  <span className="oldmed-who">{e.createdByName}</span>
                ) : null}
                {e.tag === 1 ? <span className="oldmed-tag">From Rx</span> : null}
              </div>
              <ol className="oldmed-lines">
                {(e.medicineDetails || []).map((m, j) => this._line(m, j))}
              </ol>
            </li>
          ))}
        </ol>

        {!exhausted ? (
          <div className="oldmed-foot">
            <button
              type="button"
              className="oldmed-more"
              disabled={loadingMore}
              onClick={this._more}
            >
              {loadingMore ? "Loading…" : "Show more"}
            </button>
            {total != null ? (
              <span className="oldmed-showing">
                Showing {entries.length} of {total}
              </span>
            ) : null}
          </div>
        ) : (
          <span className="oldmed-showing">
            Showing all {entries.length} {entries.length === 1 ? "entry" : "entries"}
          </span>
        )}
      </div>
    );
  }
}

OldMedicines.propTypes = {
  patientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

export default OldMedicines;
